import { OktaAuth } from '@okta/okta-auth-js';

// Okta SPA configuration. Mirrors the SSL-Okta.js pattern from the certificates
// dashboard. Accepts both Nike Okta hosts:
//   - nike.okta.com           -> PROD (auth server aus27z7p76as9Dz0H1t7)
//   - nike-qa.oktapreview.com -> QA   (auth server ausa0mcornpZLi0C40h7)
// The Nike Dev Portal creates an app in QA on initial DEV approval, and only
// mirrors it to PROD after Submit-For-Prod-Approval; we have to accept both or
// dev/test environments silently fall back to anonymous mode.
//
// Resolution order for each value:
//   1. window.__APP_CONFIG__.<KEY>     (runtime, set by docker-start-ecs-bundle.sh)
//      — authoritative when the key is defined, even if it's an empty string.
//        This lets us flip Okta on/off via a CloudFormation parameter alone,
//        without rebuilding the frontend image.
//   2. import.meta.env.<KEY>           (build-time, baked in by `vite build`)
//   3. fallback default
//
// When clientID is empty, isOktaConfigured() returns false and the app should
// short-circuit the <Security> wrapper so local dev / unconfigured deploys
// continue to work as anonymous (current behavior).

const runtime = (typeof window !== 'undefined' && window.__APP_CONFIG__) || {};

// Hosts we allow VITE_OKTA_ISSUER to point at. Anything else (a third-party
// Okta tenant, a typo, an attacker-controlled host injected via __APP_CONFIG__)
// causes isOktaConfigured() to return false so we never send tokens off-tenant.
const ALLOWED_OKTA_HOSTS = new Set([
  'nike.okta.com',           // PROD  - aus27z7p76as9Dz0H1t7
  'nike-qa.oktapreview.com', // QA    - ausa0mcornpZLi0C40h7
]);

// Pick runtime value when the key is present in __APP_CONFIG__ (the bootstrap
// always emits the key, so an empty value here is intentional "off"); fall
// back to the build-time value otherwise.
function resolve(key) {
  if (Object.prototype.hasOwnProperty.call(runtime, key)) {
    return runtime[key] || '';
  }
  return import.meta.env[key] || '';
}

const issuer = resolve('VITE_OKTA_ISSUER');
const clientID = resolve('VITE_OKTA_CLIENT_ID');

// Build the redirect URI from the current origin so the same bundle works for
// localhost, the *.sro-team-test.nikecloud.com hostname, and any future
// production hostname without rebuilding the image. The SSL-Okta.js reference
// hardcoded three branches; we delegate to window.location.origin instead.
const browserOrigin =
  typeof window !== 'undefined' ? window.location.origin : '';

// Callback path mirrors productlookupui's because we share its SPA registration
// (nike.intexp.pal). Okta verifies redirect_uri server-side with exact-match,
// and that app's allow-list lists ${origin}/pla/login URLs only -- not
// /login/callback. Keep this in lockstep with the <Route path="/pla/login">
// in App.jsx; the LoginCallback component handles whatever path lives here.
const REDIRECT_PATH = '/pla/login';

export const OktaConfig = {
  issuer,
  clientId: clientID,
  redirectUri: `${browserOrigin}${REDIRECT_PATH}`,
  scopes: ['openid', 'profile', 'email', 'offline_access'],
  pkce: true,
  responseType: 'code',
  // okta-react reads these to decide where to send the user after a successful
  // sign-in; we always land back on '/'.
  postLogoutRedirectUri: `${browserOrigin}/`,
  // Token persistence + lifecycle. Defaults are sessionStorage (per-tab) and
  // auto-renew on; we override to localStorage so tokens survive tab close /
  // window reopen / dev-server restart, which is what people expect from
  // "I signed in already". The Dev Portal registration grants both
  // Authorization Code + Refresh Token, and we request offline_access in
  // the scopes above, so okta-auth-js receives a refresh_token in the
  // token response. autoRenew=true uses that refresh_token to mint a fresh
  // access token a minute before the current one expires (per
  // expireEarlySeconds below), so the user never sees a forced sign-in
  // unless the Okta session itself has lapsed (8h per the corporate
  // sign-on policy from step 2 of the onboarding runbook). Refresh Token
  // was preferred over silent prompt=none /authorize renewal because
  // iframe-based silent renewal can break in browser configurations that
  // restrict third-party cookies; refresh_token rotation works regardless.
  // Acceptable XSS tradeoff for an internal Observability tool; a public app would
  // keep sessionStorage.
  tokenManager: {
    storage: 'localStorage',
    autoRenew: true,
    expireEarlySeconds: 60,
  },
};

export function isOktaConfigured() {
  // The issuer must point at one of the Nike-corporate Okta hosts (PROD or
  // QA) and a clientId must be present. We refuse to enable Okta against any
  // other host so a misconfigured env var can't silently send tokens to a
  // third party.
  if (!issuer || !clientID) return false;
  try {
    const u = new URL(issuer);
    return ALLOWED_OKTA_HOSTS.has(u.hostname);
  } catch {
    return false;
  }
}

export function debugOktaConfig() {
  // Returned (not logged) so the caller decides where it lands. Used by the
  // dev-only debug surface in the Sidebar footer.
  return {
    issuer,
    clientId: clientID,
    redirectUri: OktaConfig.redirectUri,
    configured: isOktaConfigured(),
  };
}

// Lazily-created singleton so api/client.js, App.jsx, and helpers all share
// one OktaAuth instance \u2014 multiple instances would each maintain their own
// token cache and silent-renewal loop, racing each other and burning Okta
// rate limits. Returns null when Okta isn't configured so callers can skip
// adding Authorization headers entirely.
let oktaAuthSingleton = null;

export function getOktaAuth() {
  if (!isOktaConfigured()) return null;
  if (!oktaAuthSingleton) {
    oktaAuthSingleton = new OktaAuth(OktaConfig);
  }
  return oktaAuthSingleton;
}
